import { GOAL_FRAME, laneTargets, shotBandThresholds } from './constants';
import { clamp, createShotSeed, deterministicRoll, getPreviewLane, getPreviewTarget, getShotBand } from './helpers';
import {
  EngineState,
  KeeperAction,
  Particle,
  ShotBand,
  ShotLane,
  ShotOutcome,
  ShotResolution,
} from './types';

// CBG boundary: band -> outcome -> keeper action is authoritative gameplay state.
const shotRulesV1 = {
  green: { goalChance: 1, saveChance: 0, missChance: 0 },
  yellow: { goalChanceMin: 0.38, goalChanceMax: 0.66, centerMissMin: 0.08, centerMissMax: 0.14, sideMissMin: 0.12, sideMissMax: 0.2 },
  red: { lowSaveChance: 0.68, highSaveChance: 0.34 },
} as const;

const oppositeLane = (lane: ShotLane): ShotLane => {
  if (lane === 'center') {
    return 'left';
  }

  return lane === 'left' ? 'right' : 'left';
};

const sideLaneFromRoll = (roll: number): ShotLane => (roll < 0.5 ? 'left' : 'right');

const getEdgeRisk = (aim: number) => clamp((Math.abs(aim - 0.5) - 0.12) / 0.38, 0, 1);

const resolveOutcome = (band: ShotBand, lane: ShotLane, quality: number, power: number, aim: number, roll: number): ShotOutcome => {
  const edgeRisk = getEdgeRisk(aim);
  if (band === 'green') {
    return 'goal';
  }

  if (band === 'red') {
    if (power >= shotBandThresholds.redHighMin) {
      return 'miss';
    }
    const saveChanceBase = power < 0.5 ? shotRulesV1.red.lowSaveChance : shotRulesV1.red.highSaveChance;
    const saveChance = clamp(saveChanceBase + edgeRisk * 0.08, 0, 0.9);
    return roll < saveChance ? 'save' : 'miss';
  }

  const confidence = clamp((quality - 0.42) / 0.4, 0, 1);
  const sideLane = lane !== 'center';
  const baseGoalChance = sideLane
    ? shotRulesV1.yellow.goalChanceMin + confidence * (shotRulesV1.yellow.goalChanceMax - shotRulesV1.yellow.goalChanceMin)
    : 0.4 + confidence * 0.26;
  const goalChance = clamp(baseGoalChance - (sideLane ? 0.06 : 0.02) - edgeRisk * (sideLane ? 0.16 : 0.06), 0.18, 0.72);
  const missChance =
    lane === 'center'
      ? shotRulesV1.yellow.centerMissMax - confidence * (shotRulesV1.yellow.centerMissMax - shotRulesV1.yellow.centerMissMin) + edgeRisk * 0.05
      : shotRulesV1.yellow.sideMissMax - confidence * (shotRulesV1.yellow.sideMissMax - shotRulesV1.yellow.sideMissMin) + 0.04 + edgeRisk * 0.09;
  const saveChance = Math.max(0, 1 - goalChance - missChance);

  if (roll < goalChance) return 'goal';
  if (roll < goalChance + saveChance) return 'save';
  return 'miss';
};

const resolveKeeperPlan = (
  band: ShotBand,
  lane: ShotLane,
  outcome: ShotOutcome,
  wrongSideRoll: number,
): { keeperAction: KeeperAction; keeperLane: ShotLane } => {
  if (outcome === 'save') {
    return { keeperAction: 'save', keeperLane: lane };
  }

  if (outcome === 'miss') {
    return { keeperAction: 'hold', keeperLane: 'center' };
  }

  if (lane === 'center') {
    return { keeperAction: 'wrong-side', keeperLane: sideLaneFromRoll(wrongSideRoll) };
  }

  if (band === 'green' && wrongSideRoll < 0.82) {
    return { keeperAction: 'wrong-side', keeperLane: oppositeLane(lane) };
  }

  if (band === 'yellow' && wrongSideRoll < 0.55) {
    return { keeperAction: 'late', keeperLane: 'center' };
  }

  return { keeperAction: 'wrong-side', keeperLane: oppositeLane(lane) };
};

const resolveMissTarget = (
  lane: ShotLane,
  power: number,
  target: { x: number; y: number; z: number },
  roll: (salt: number) => number,
  rollRange: (salt: number, min: number, max: number) => number,
) => {
  const overBar = power >= shotBandThresholds.redHighMin;
  if (overBar) {
    return {
      x: target.x + rollRange(10, -0.22, 0.22),
      y: GOAL_FRAME.crossbarY + rollRange(11, 0.42, 0.98),
      z: GOAL_FRAME.frontZ + rollRange(12, 0.08, 0.62),
    };
  }

  const missDirection = lane === 'center' ? (roll(10) < 0.5 ? -1 : 1) : lane === 'left' ? -1 : 1;
  return {
    x: missDirection * (GOAL_FRAME.halfWidth + rollRange(11, 0.58, 1.26)),
    y: clamp(target.y - rollRange(12, 0.18, 0.42), 0.56, GOAL_FRAME.crossbarY - 0.18),
    z: GOAL_FRAME.frontZ + rollRange(13, 0.08, 0.62),
  };
};

export const resolveShot = (aim: number, power: number, shotId: number): ShotResolution => {
  const band = getShotBand(power);
  const perfect = band === 'green';
  const seed = createShotSeed(shotId, power);
  const roll = (salt: number) => deterministicRoll(seed, salt);
  const rollRange = (salt: number, min: number, max: number) => min + roll(salt) * (max - min);
  const lane = getPreviewLane(aim);
  const distanceFromStrikeWindow =
    power < shotBandThresholds.greenStart
      ? shotBandThresholds.greenStart - power
      : power > shotBandThresholds.greenEnd
        ? power - shotBandThresholds.greenEnd
        : 0;
  const quality =
    perfect
      ? 1
      : band === 'yellow'
        ? clamp(1 - distanceFromStrikeWindow / 0.28, 0.36, 0.8)
        : clamp(1 - distanceFromStrikeWindow / 0.64, 0.14, 0.42);
  const baseTarget = getPreviewTarget(aim, power);
  let target = {
    x: baseTarget.x + rollRange(2, -0.26, 0.26) * (1 - quality),
    y: baseTarget.y + rollRange(3, -0.12, 0.12) * (1 - quality),
    z: baseTarget.z,
  };

  if (band === 'yellow') {
    target.y = Math.max(0.92, target.y - 0.22);
  }

  if (band === 'red' && power <= shotBandThresholds.redLowMax) {
    target.y = Math.max(0.62, target.y - 0.34);
  }

  const outcome = resolveOutcome(band, lane, quality, power, aim, roll(4));
  const { keeperAction, keeperLane } = resolveKeeperPlan(band, lane, outcome, roll(5));

  if (outcome === 'goal') {
    if (band === 'green') {
      target = {
        x: target.x + (lane === 'left' ? -0.24 : lane === 'right' ? 0.24 : 0),
        y: target.y + (lane === 'center' ? 0.08 : 0.14),
        z: target.z,
      };
    } else {
      target = {
        x: target.x + rollRange(6, -0.1, 0.1),
        y: target.y + 0.05,
        z: target.z,
      };
    }
  }

  if (outcome === 'miss') {
    target = resolveMissTarget(lane, power, target, roll, rollRange);
  }

  const missKind = outcome === 'miss' ? (power >= shotBandThresholds.redHighMin ? 'high' : 'wide') : null;
  return {
    id: shotId,
    band,
    lane,
    keeperAction,
    keeperLane,
    power,
    quality,
    timing: power,
    perfect,
    outcome,
    missKind,
    target,
    saveProgress: outcome === 'save' ? rollRange(8, 0.58, 0.7) : rollRange(9, 0.72, 0.82),
  };
};

export const spawnOutcomeParticles = (engine: EngineState, shot: ShotResolution) => {
  const seed = createShotSeed(shot.id, shot.power);
  const roll = (salt: number) => deterministicRoll(seed, 100 + salt);
  const rollRange = (salt: number, min: number, max: number) => min + roll(salt) * (max - min);
  const burstCount = shot.perfect ? 34 : shot.outcome === 'goal' ? 22 : shot.outcome === 'save' ? 18 : 14;
  const palette =
    shot.outcome === 'goal'
      ? shot.perfect
        ? ['#fef08a', '#c4b5fd', '#34d399', '#f8fafc']
        : ['#c7d2fe', '#60a5fa', '#34d399']
      : shot.outcome === 'save'
        ? ['#bfdbfe', '#38bdf8', '#f8fafc']
        : ['#fde68a', '#f97316', '#f8fafc'];
  const origin = shot.outcome === 'save' ? { x: laneTargets[shot.keeperLane].x * 0.62, y: Math.max(1.1, shot.target.y * 0.8), z: 24.1 } : shot.target;

  const particles = Array.from({ length: burstCount }, (_, index) => {
    const spread = shot.perfect ? 3.8 : shot.outcome === 'goal' ? 2.8 : 2;
    return {
      position: { ...origin },
      velocity: { x: rollRange(index * 5 + 1, -spread, spread), y: rollRange(index * 5 + 2, 0.8, 3.8), z: rollRange(index * 5 + 3, -spread * 0.7, spread * 0.6) },
      color: palette[Math.min(palette.length - 1, Math.floor(roll(index * 5 + 4) * palette.length))],
      radius: rollRange(index * 5 + 5, 0.08, shot.perfect ? 0.28 : 0.22),
      lifeMs: 0,
      maxLifeMs: rollRange(index * 5 + 6, 360, shot.perfect ? 920 : 720),
    } satisfies Particle;
  });

  engine.particles.push(...particles);
};

export const updateParticles = (engine: EngineState, deltaMs: number) => {
  const deltaSeconds = deltaMs / 1000;
  engine.particles = engine.particles
    .map((particle) => ({
      ...particle,
      lifeMs: particle.lifeMs + deltaMs,
      velocity: { x: particle.velocity.x * 0.986, y: particle.velocity.y - 7.4 * deltaSeconds, z: particle.velocity.z * 0.984 },
      position: {
        x: particle.position.x + particle.velocity.x * deltaSeconds,
        y: Math.max(0.05, particle.position.y + particle.velocity.y * deltaSeconds),
        z: particle.position.z + particle.velocity.z * deltaSeconds,
      },
    }))
    .filter((particle) => particle.lifeMs < particle.maxLifeMs);
};
